import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import "../styles/busyOverlay.css";

interface BusyContextType {
  isBusy: boolean;
  startBusy: () => void;
  stopBusy: () => void;
  runBusy: <T>(task: () => Promise<T>) => Promise<T>;
}

const BusyContext = createContext<BusyContextType | undefined>(undefined);

export const BusyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [busyCount, setBusyCount] = useState(0);

  const startBusy = useCallback(() => {
    setBusyCount((prev) => prev + 1);
  }, []);

  const stopBusy = useCallback(() => {
    setBusyCount((prev) => Math.max(0, prev - 1));
  }, []);

  const runBusy = useCallback(
    async <T,>(task: () => Promise<T>): Promise<T> => {
      try {
        startBusy();
        return await task();
      } finally {
        stopBusy();
      }
    },
    [startBusy, stopBusy],
  );

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        startBusy();
        return config;
      },
      (error) => {
        stopBusy();
        return Promise.reject(error);
      },
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        stopBusy();
        return response;
      },
      (error) => {
        stopBusy();
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [startBusy, stopBusy]);

  const value = useMemo(
    () => ({
      isBusy: busyCount > 0,
      startBusy,
      stopBusy,
      runBusy,
    }),
    [busyCount, startBusy, stopBusy, runBusy],
  );

  return (
    <BusyContext.Provider value={value}>
      {children}

      {busyCount > 0 && (
        <div className="app-busy-overlay">
          <div className="app-busy-box">
            <div className="app-busy-spinner"></div>
            <p>Please wait...</p>
          </div>
        </div>
      )}
    </BusyContext.Provider>
  );
};

export const useBusy = () => {
  const context = useContext(BusyContext);

  if (!context) {
    throw new Error("useBusy must be used inside BusyProvider");
  }

  return context;
};
